// IndexedDB wrapper for offline caching
type CacheType = 'flashcard' | 'clinical_case' | 'calculator' | 'revision_sheet' | 'patient' | 'consultation' | 'page' | 'asset' | 'flashcard_category' | 'checklist';

interface CacheItem {
  id: string;
  type: CacheType;
  data: any;
  lastAccessed: number;
  expiresAt?: number;
}

class OfflineCache {
  private dbName = 'sclepios-offline-cache';
  private version = 1;
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create object stores
        if (!db.objectStoreNames.contains('cache')) {
          const cacheStore = db.createObjectStore('cache', { keyPath: 'id' });
          cacheStore.createIndex('type', 'type', { unique: false });
          cacheStore.createIndex('lastAccessed', 'lastAccessed', { unique: false });
        }
      };
    });
  }

  async setItem(item: CacheItem): Promise<void> {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cache'], 'readwrite');
      const store = transaction.objectStore('cache');
      const request = store.put({
        ...item,
        lastAccessed: Date.now()
      });

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async getItem(id: string): Promise<CacheItem | null> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cache'], 'readwrite');
      const store = transaction.objectStore('cache');
      const request = store.get(id);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const item = request.result;
        if (item) {
          // Update last accessed time
          item.lastAccessed = Date.now();
          store.put(item);
          
          // Check if expired
          if (item.expiresAt && Date.now() > item.expiresAt) {
            this.removeItem(id);
            resolve(null);
          } else {
            resolve(item);
          }
        } else {
          resolve(null);
        }
      };
    });
  }

  async getItemsByType(type: CacheItem['type'], limit = 50): Promise<CacheItem[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cache'], 'readonly');
      const store = transaction.objectStore('cache');
      const index = store.index('type');
      const request = index.getAll(type);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const items = request.result
          .filter(item => !item.expiresAt || Date.now() < item.expiresAt)
          .sort((a, b) => b.lastAccessed - a.lastAccessed)
          .slice(0, limit);
        resolve(items);
      };
    });
  }

  async removeItem(id: string): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cache'], 'readwrite');
      const store = transaction.objectStore('cache');
      const request = store.delete(id);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async clear(): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cache'], 'readwrite');
      const store = transaction.objectStore('cache');
      const request = store.clear();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async getCacheSize(): Promise<number> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cache'], 'readonly');
      const store = transaction.objectStore('cache');
      const request = store.count();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
    });
  }

  async cleanupOldItems(maxAge = 7 * 24 * 60 * 60 * 1000): Promise<void> {
    if (!this.db) await this.init();

    const cutoffTime = Date.now() - maxAge;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cache'], 'readwrite');
      const store = transaction.objectStore('cache');
      const index = store.index('lastAccessed');
      const request = index.openCursor(IDBKeyRange.upperBound(cutoffTime));

      request.onerror = () => reject(request.error);
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };
    });
  }

  // Cache specific data types
  async cacheFlashcard(flashcard: any): Promise<void> {
    await this.setItem({
      id: `flashcard_${flashcard.id}`,
      type: 'flashcard',
      data: flashcard,
      lastAccessed: Date.now(),
      expiresAt: Date.now() + (24 * 60 * 60 * 1000) // 24 hours
    });
  }

  async cacheClinicalCase(clinicalCase: any): Promise<void> {
    await this.setItem({
      id: `clinical_case_${clinicalCase.id}`,
      type: 'clinical_case',
      data: clinicalCase,
      lastAccessed: Date.now(),
      expiresAt: Date.now() + (7 * 24 * 60 * 60 * 1000) // 7 days
    });
  }

  async cacheCalculator(calculator: any): Promise<void> {
    await this.setItem({
      id: `calculator_${calculator.id}`,
      type: 'calculator',
      data: calculator,
      lastAccessed: Date.now()
      // No expiration for calculators
    });
  }

  async getCachedFlashcards(): Promise<any[]> {
    const items = await this.getItemsByType('flashcard');
    return items.map(item => item.data);
  }

  async getCachedClinicalCases(): Promise<any[]> {
    const items = await this.getItemsByType('clinical_case', 20);
    return items.map(item => item.data);
  }

  async getCachedCalculators(): Promise<any[]> {
    const items = await this.getItemsByType('calculator');
    return items.map(item => item.data);
  }

  // Cache specific methods for consultations
  async cachePatient(patient: any) {
    await this.setItem({
      id: `patient_${patient.id}`,
      type: 'patient',
      data: patient,
      lastAccessed: Date.now()
    });
  }

  async cacheConsultation(consultation: any) {
    await this.setItem({
      id: `consultation_${consultation.id}`,
      type: 'consultation', 
      data: consultation,
      lastAccessed: Date.now()
    });
  }

  async getCachedPatients() {
    return this.getItemsByType('patient');
  }

  async getCachedConsultations() {
    return this.getItemsByType('consultation');
  }

  async getCachedPatient(patientId: string) {
    const item = await this.getItem(`patient_${patientId}`);
    return item?.data || null;
  }

  async getCachedConsultation(consultationId: string) {
    const item = await this.getItem(`consultation_${consultationId}`);
    return item?.data || null;
  }
}

export const offlineCache = new OfflineCache();